<script setup lang="ts">
import { useBiometricAuth } from '~/composables/useBiometricAuth';
import { useBiometricColdStartGate } from '~/composables/useBiometricColdStartGate';

/**
 * Gate de biometria no cold start, estilo "Betano".
 *
 * Fluxo:
 *  1. Marca `isGateInProgress=true` ANTES de qualquer coisa — o
 *     ApiService usa esse flag para suprimir toasts/redirects de 401
 *     enquanto o prompt biometrico esta na tela.
 *  2. Renderiza overlay transparente fullscreen para impedir cliques
 *     na home enquanto o prompt nativo do SO esta aberto.
 *  3. Se ja existir sessao ativa (cookie zumbi), invalida proativamente
 *     via logout({ origin: 'system' }) ANTES do prompt biometrico —
 *     garante estado limpo no re-login e elimina respostas de
 *     "token has been blacklisted" do backend JWT.
 *  4. Dispara `unlock()`.
 *  5. OK → re-submete /auth/login via store (sempre, ja em estado limpo).
 *  6. Cancel/falha → logout silencioso (origin='system'), credencial
 *     preservada (nao marca biometric_locked) para retry pelo botao
 *     do Login.vue.
 *
 * Sem dismiss-on-click: o overlay captura cliques para que o usuario
 * nao consiga interagir com a home enquanto o prompt esta na tela.
 */

const {
  isAvailableEnvironment,
  checkAvailability,
  unlock,
  isLockedFlagSet,
  isDisabledFlagSet,
} = useBiometricAuth();

const { shouldFire, markFired, beginGate, endGate } =
  useBiometricColdStartGate();

const { $metrics } = useNuxtApp();
const { isAuthenticated } = storeToRefs(useAuthStore());
const { login: storeLogin, logout: storeLogout } = useAuthStore();
const { isAppSource } = useLayout();
const { openToaster } = useLayoutStore();

const overlayVisible = ref(false);
const isLogging = ref(false);

async function runGate() {
  if (!shouldFire()) {
    return;
  }

  if (!isAvailableEnvironment()) {
    return;
  }

  markFired();
  beginGate();

  try {
    const availability = await checkAvailability();

    if (!availability.hasHardware) {
      return;
    }

    if (!availability.isEnrolled) {
      return;
    }

    if (!availability.hasCredential) {
      return;
    }

    if (isLockedFlagSet()) {
      return;
    }

    if (isDisabledFlagSet()) {
      return;
    }

    // Vai pedir biometria — bloqueia interacao com a home a partir
    // daqui (antes do logout preemptivo, que pode demorar).
    overlayVisible.value = true;

    // Sessao zumbi: cookie ainda existe no WebView mas o backend pode ter
    // o JWT blacklisted. Invalida proativamente antes do prompt, para que
    // o re-login pos-biometria parta de um estado limpo. Logout silencioso
    // (origin=system) NAO marca biometric_locked.
    if (isAuthenticated.value) {
      try {
        await storeLogout({
          displayMessage: false,
          redirectToHome: false,
          origin: 'system',
        });
      } catch {}
    }

    const result = await unlock();

    if (!result.success || !result.login || !result.password) {
      if (result.error === 'biometric_changed') {
        openToaster({
          message:
            'Detectamos uma alteração na biometria do aparelho. Faça login e ative a biometria novamente.',
          type: 'warning',
        });
      }

      // Ja fizemos logout preemptivo acima, mas mantemos esta chamada
      // como garantia (idempotente quando ja deslogado).
      try {
        await storeLogout({
          displayMessage: false,
          redirectToHome: false,
          origin: 'system',
        });
      } catch {}

      return;
    }

    isLogging.value = true;

    try {
      const response = await storeLogin({
        login: result.login,
        email: result.login,
        password: result.password,
        app_source: isAppSource.value ? 'app' : 'web',
        captcha_token: '',
      });

      $metrics.push('appsflyer')?.handle('af_login', {
        af_login_method: 'biometric',
        _customer_user_id: response?.data?.user?.id,
      });
    } catch {
      try {
        await storeLogout({
          displayMessage: false,
          redirectToHome: false,
          origin: 'system',
        });
      } catch {}
    }
  } finally {
    overlayVisible.value = false;
    isLogging.value = false;
    endGate();
  }
}

// Listener seguro: bloqueia eventos de mouse/touch enquanto o overlay
// esta visivel. Pure capture, sem dismiss-on-click.
function swallow(e: Event): void {
  e.stopPropagation();
  e.preventDefault();
}

onMounted(() => {
  runGate();
});
</script>

<template>
  <Teleport
    v-if="overlayVisible"
    to="body"
  >
    <div
      aria-hidden="true"
      class="biometric-gate-overlay"
      :class="{ 'biometric-gate-overlay--loading': isLogging }"
      @click.capture="swallow"
      @pointerdown.capture="swallow"
      @touchend.capture="swallow"
      @touchstart.capture="swallow"
    >
      <BaseLoadingIconLoader
        v-if="isLogging"
        duration="500ms"
        :show="true"
        size="40px"
      />
    </div>
  </Teleport>
</template>

<style scoped>
.biometric-gate-overlay {
  position: fixed;
  inset: 0;
  z-index: 2147483647;
  background: transparent;
  touch-action: none;
  -webkit-tap-highlight-color: transparent;
}

.biometric-gate-overlay--loading {
  background: #030a11;
  display: flex;
  align-items: center;
  justify-content: center;
}
</style>
